import { Text, View } from '@react-pdf/renderer';
import partition from 'lodash/partition';
import React from 'react';

import { tw } from '../pdf-tw';
import { commonStyles } from '../styles';

import {
  Anchor,
  Bold,
  InlineCode,
  Italic,
  Paragraph,
  Span,
  Strikethrough,
  Underline,
} from './components';

/** Elements that can be rendered inline */
const INLINE_TYPES = new Set<any>([
  Text,
  Paragraph,
  Span,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  InlineCode,
  Anchor,
]);

// keep raw strings wrapped in <Text> (React-PDF requirement),
// and separate inline text (<Text> nodes) from block children (e.g., nested <ul> Views)
const normalizeToText = (nodes: React.ReactNode) =>
  React.Children.toArray(nodes).map((child, idx) =>
    typeof child === 'string' ? (
      <Text style={tw(commonStyles.body())} key={`t-${idx}`}>
        {child}
      </Text>
    ) : (
      child
    ),
  );

const partitionInlineAndBlocks = (nodes: React.ReactNode[]) => {
  const [inline, blocks] = partition(nodes, (child) =>
    INLINE_TYPES.has((child as any).type),
  );

  return {
    inline,
    blocks,
  };
};

// Helper function to convert number to lowercase letter (a-z)
const numberToLetter = (num: number): string => {
  return String.fromCharCode(97 + (num % 26)); // wrap after 'z'
};

// Helper function to convert number to lowercase roman numeral
const numberToRoman = (num: number): string => {
  const romanNumerals = [
    'i',
    'ii',
    'iii',
    'iv',
    'v',
    'vi',
    'vii',
    'viii',
    'ix',
    'x',
  ];
  return romanNumerals[num] ?? 'i';
};

export const UnorderedList = ({ children }: { children: React.ReactNode }) => {
  // Clone children and pass listType and nestingLevel to ListItem components
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        listType: 'unordered',
      } as any);
    }

    return child;
  });

  return <View style={tw('flex flex-col ml-3')}>{childrenWithProps}</View>;
};

export const OrderedList = ({
  children,
  nestingLevel = 0,
}: {
  children: React.ReactNode;
  nestingLevel?: number;
}) => {
  // Clone children and pass listType, nestingLevel, and index to ListItem components
  let itemIndex = 0;
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const props = {
        listType: 'ordered',
        itemIndex: itemIndex++,
        nestingLevel,
      };
      return React.cloneElement(child, props);
    }

    return child;
  });

  return <View style={tw('flex flex-col ml-3')}>{childrenWithProps}</View>;
};

export const ListItem = ({
  children,
  listType = 'unordered',
  itemIndex = 0,
  nestingLevel = 0,
}: {
  children: React.ReactNode;
  listType?: 'ordered' | 'unordered';
  itemIndex?: number;
  nestingLevel?: number;
}) => {
  const normalized = normalizeToText(children);
  const { inline, blocks } = partitionInlineAndBlocks(normalized);

  // Cycle through 3 levels, then repeat (0, 1, 2, 0, 1, 2, ...)
  const level = nestingLevel % 3;

  // Determine marker based on list type and nesting level
  let marker: string;
  if (listType === 'ordered') {
    // Ordered list markers: 1. -> a. -> i. -> repeat
    if (level === 0) {
      marker = `${itemIndex + 1}.`;
    } else if (level === 1) {
      marker = `${numberToLetter(itemIndex)}.`;
    } else {
      marker = `${numberToRoman(itemIndex)}.`;
    }
  } else {
    // Unordered list markers: always use filled bullet
    marker = '•';
  }

  // Width for marker gutter (numbers and letters need more space)
  const gutterWidth = listType === 'ordered' ? 'w-4' : 'w-3';

  // Clone nested list blocks and increment their nesting level
  const nestedBlocks = React.Children.map(blocks, (child) => {
    if (React.isValidElement(child)) {
      // Check if it's a list component (UnorderedList or OrderedList)
      const childType = (child as any).type;
      if (childType === UnorderedList || childType === OrderedList) {
        return React.cloneElement(child, {
          nestingLevel: nestingLevel + 1,
        } as any);
      }
    }
    return child;
  });

  return (
    <View style={tw('flex flex-col')}>
      {/* marker + inline text */}
      <View style={tw('flex flex-row items-start')}>
        {/* marker gutter keeps wrapped lines aligned */}
        <View style={tw(gutterWidth)}>
          <Text style={tw(commonStyles.body({ className: 'leading-tight' }))}>
            {marker}
          </Text>
        </View>

        {/* inline text */}
        <Text style={tw(commonStyles.body({ className: 'flex-1' }))}>
          {inline.length ? inline : ' '}
        </Text>
      </View>

      {/* nested blocks (e.g., a nested <ul> or <ol>) */}
      {nestedBlocks && nestedBlocks.length > 0 && (
        <View style={tw('ml-3')}>{nestedBlocks}</View>
      )}
    </View>
  );
};
